import { TEAM_NAMES } from "./team-config";
import type {
  BootstrapData,
  EventRecord,
  PaymentRecord,
  Routine,
  WeeklyClass,
} from "./types";
import { paymentQuote } from "./payment-pricing";
export { HST_RATE_BPS, calculateHstCents } from "./payment-pricing";

const weekdays = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

function escapeIcs(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

function compactDate(date: string): string {
  return date.replaceAll("-", "");
}

function localStamp(date: string, time: string): string {
  return `${compactDate(date)}T${time.replace(":", "")}00`;
}

function addDay(date: string): string {
  const value = new Date(`${date}T12:00:00Z`);
  value.setUTCDate(value.getUTCDate() + 1);
  return value.toISOString().slice(0, 10);
}

function nextWeekday(weekday: number): string {
  const current = new Date();
  const delta = (weekday - current.getUTCDay() + 7) % 7;
  current.setUTCDate(current.getUTCDate() + delta);
  return current.toISOString().slice(0, 10);
}

export function parseQuickEventText(
  text: string,
  today: string,
): Pick<EventRecord, "title" | "type" | "date" | "showTime" | "venue"> {
  const trimmed = text.trim();
  const lower = trimmed.toLowerCase();
  const type: EventRecord["type"] = lower.includes("competition")
    ? "competition"
    : lower.includes("team event")
      ? "team-event"
      : "performance";
  const dateMatch = trimmed.match(/\b(20\d{2}-\d{2}-\d{2})\b/);
  const tomorrow = new Date(`${today}T12:00:00Z`);
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
  const relative = lower.includes("tomorrow")
    ? tomorrow.toISOString().slice(0, 10)
    : lower.includes("today")
      ? today
      : undefined;
  const timeMatch = lower.match(/\bat\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\b/);
  let showTime: string | undefined;
  if (timeMatch) {
    let hour = Number(timeMatch[1]);
    if (timeMatch[3] === "pm" && hour < 12) hour += 12;
    if (timeMatch[3] === "am" && hour === 12) hour = 0;
    showTime = `${String(hour).padStart(2, "0")}:${timeMatch[2] ?? "00"}`;
  }
  const withoutDateTime = trimmed
    .replace(/\b(20\d{2}-\d{2}-\d{2}|today|tomorrow)\b/gi, "")
    .replace(/\bat\s+\d{1,2}(?::\d{2})?\s*(?:am|pm)?\b/i, "")
    .replace(/\s+/g, " ")
    .trim();
  const venueMatch = withoutDateTime.match(/\s+at\s+(.+)$/i);
  const venue = venueMatch?.[1]?.trim() ?? "";
  const title = (
    venueMatch
      ? withoutDateTime.slice(0, venueMatch.index).trim()
      : withoutDateTime
  ).replace(/^(schedule|add)\s+/i, "");
  return {
    title: title || venue || "Untitled event",
    type,
    date: dateMatch?.[1] ?? relative,
    showTime,
    venue,
  };
}

function routineNames(event: EventRecord, routines: Routine[]): string[] {
  return event.routines
    .map((entry) => {
      const routine = routines.find((item) => item.id === entry.routineId);
      return routine?.name ?? entry.routineName;
    })
    .filter((name): name is string => Boolean(name));
}

function alarm(trigger: string, description: string): string[] {
  return [
    "BEGIN:VALARM",
    `TRIGGER:${trigger}`,
    "ACTION:DISPLAY",
    `DESCRIPTION:${escapeIcs(description)}`,
    "END:VALARM",
  ];
}

function classLines(item: WeeklyClass): string[] {
  const first = nextWeekday(item.weekday);
  return [
    "BEGIN:VEVENT",
    `UID:${item.id}@avante.arshan.to`,
    `DTSTAMP:${item.updatedAt.replace(/[-:]/g, "").replace(/\.\d{3}/, "")}`,
    `DTSTART;TZID=America/Toronto:${localStamp(first, item.startTime)}`,
    `DTEND;TZID=America/Toronto:${localStamp(first, item.endTime)}`,
    `RRULE:FREQ=WEEKLY;BYDAY=${weekdays[item.weekday]}`,
    `SUMMARY:Avante ${TEAM_NAMES[item.teamId]} practice`,
    `LOCATION:${escapeIcs([item.venue, item.address].filter(Boolean).join(", "))}`,
    "DESCRIPTION:Weekly Avante performance-team practice.",
    "STATUS:CONFIRMED",
    ...alarm("-PT1H", "Avante practice starts in one hour"),
    "END:VEVENT",
  ];
}

function eventLines(
  event: EventRecord,
  routines: Routine[],
  origin: string,
): string[] {
  if (!event.date) return [];
  const names = routineNames(event, routines);
  const times = event.callTime || event.showTime;
  const lines = [
    "BEGIN:VEVENT",
    `UID:${event.id}@avante.arshan.to`,
    `DTSTAMP:${event.updatedAt.replace(/[-:]/g, "").replace(/\.\d{3}/, "")}`,
  ];
  if (times) {
    lines.push(
      `DTSTART;TZID=America/Toronto:${localStamp(event.date, times)}`,
      `DTEND;TZID=America/Toronto:${localStamp(event.date, event.endTime || event.showTime || times)}`,
    );
  } else {
    lines.push(
      `DTSTART;VALUE=DATE:${compactDate(event.date)}`,
      `DTEND;VALUE=DATE:${compactDate(addDay(event.date))}`,
    );
  }
  lines.push(
    `SUMMARY:${escapeIcs(`${event.type === "practice" ? "Practice" : event.type === "competition" ? "Competition" : event.type === "team-event" ? "Team event" : "Performance"}: ${event.title}`)}`,
    `LOCATION:${escapeIcs([event.venue, event.address].filter(Boolean).join(", "))}`,
    `DESCRIPTION:${escapeIcs(event.type === "practice" ? `${event.showTime || event.callTime || "TBD"} - ${event.endTime || "TBD"}. ${event.notes}` : `${event.callTime ? `Call ${event.callTime}. ` : "Call time TBD. "}${event.showTime ? `Show ${event.showTime}. ` : "Show time TBD. "}${names.join(", ")}. ${event.notes}`)}`,
    `URL:${origin}/?event=${encodeURIComponent(event.id)}`,
    `STATUS:${event.status === "cancelled" ? "CANCELLED" : "CONFIRMED"}`,
    ...alarm("-P1D", "Avante event tomorrow"),
    "END:VEVENT",
  );
  return lines;
}

function paymentLines(payment: PaymentRecord): string[] {
  if (payment.status !== "due" || !payment.dueDate) return [];
  const quote = paymentQuote(payment);
  const remaining = Math.max(
    0,
    quote.electronicCents - (payment.paidCents ?? 0),
  );
  const description =
    payment.amount == null && payment.amountCents == null
      ? "Check with your Coach for the amount."
      : quote.conditional
        ? `E-transfer: $${(quote.electronicCents / 100).toFixed(2)} CAD including 13% HST. Cash: $${(quote.cashCents / 100).toFixed(2)} CAD.`
        : `Remaining: $${(remaining / 100).toFixed(2)} CAD.${payment.cashOnly ? " Cash only." : ""}`;
  return [
    "BEGIN:VEVENT",
    `UID:${payment.id}@payments.avante.arshan.to`,
    `DTSTAMP:${payment.updatedAt.replace(/[-:]/g, "").replace(/\.\d{3}/, "")}`,
    `DTSTART;VALUE=DATE:${compactDate(payment.dueDate)}`,
    `DTEND;VALUE=DATE:${compactDate(addDay(payment.dueDate))}`,
    `SUMMARY:${escapeIcs(`Payment due: ${payment.label}`)}`,
    `DESCRIPTION:${escapeIcs(description)}`,
    "STATUS:CONFIRMED",
    ...alarm("-P1D", "Avante payment is due"),
    "END:VEVENT",
  ];
}

export function buildCalendar(data: BootstrapData, origin: string): string {
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Avante Dance Company//Team Hub//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    `X-WR-CALNAME:Avante ${data.user.name}`,
    "X-WR-TIMEZONE:America/Toronto",
    "REFRESH-INTERVAL;VALUE=DURATION:PT4H",
    "X-PUBLISHED-TTL:PT4H",
  ];
  if (data.calendarPreferences.weeklyPractices)
    for (const item of data.classes.filter((entry) => entry.active))
      lines.push(...classLines(item));
  for (const event of data.events.filter((entry) => {
    if (entry.status === "draft") return false;
    if (entry.type === "practice")
      return data.calendarPreferences.weeklyPractices;
    if (entry.type === "team-event") return data.calendarPreferences.teamEvents;
    if (entry.type === "competition")
      return data.calendarPreferences.competitions;
    if (data.calendarPreferences.performances === "none") return false;
    if (data.calendarPreferences.performances === "all") return true;
    return entry.routines.some((routine) =>
      routine.performers.some(
        (performer) => performer.memberId === data.user.id,
      ),
    );
  }))
    lines.push(...eventLines(event, data.routines, origin));
  for (const payment of data.payments.filter(
    (entry) => entry.memberId === data.user.id,
  ))
    lines.push(...paymentLines(payment));
  lines.push("END:VCALENDAR", "");
  return lines.join("\r\n");
}

export function googleEventUrl(
  event: EventRecord,
  routines: Routine[],
): string {
  if (!event.date) return "";
  const startTime = event.callTime || event.showTime;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `${event.type === "practice" ? "Practice" : event.type === "competition" ? "Competition" : event.type === "team-event" ? "Team event" : "Performance"}: ${event.title}`,
    details: `${routineNames(event, routines).join(", ")}${event.notes ? `\n\n${event.notes}` : ""}`,
    location: [event.venue, event.address].filter(Boolean).join(", "),
    ctz: "America/Toronto",
  });
  params.set(
    "dates",
    startTime
      ? `${localStamp(event.date, startTime)}/${localStamp(event.date, event.endTime || event.showTime || startTime)}`
      : `${compactDate(event.date)}/${compactDate(addDay(event.date))}`,
  );
  return `https://calendar.google.com/calendar/render?${params}`;
}

export function googleClassUrl(item: WeeklyClass): string {
  const first = nextWeekday(item.weekday);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `Avante ${TEAM_NAMES[item.teamId]} practice`,
    dates: `${localStamp(first, item.startTime)}/${localStamp(first, item.endTime)}`,
    recur: `RRULE:FREQ=WEEKLY;BYDAY=${weekdays[item.weekday]}`,
    details: "Weekly Avante performance-team practice.",
    location: [item.venue, item.address].filter(Boolean).join(", "),
    ctz: "America/Toronto",
  });
  return `https://calendar.google.com/calendar/render?${params}`;
}
